/**
 * Générateur — Connaître et justifier les situations dans lesquelles des
 * parenthèses sont indispensables au sens des écritures.
 * Attendu ID : 711 | Niveau : 5ème | Domaine : Nombres relatifs (cluster 5)
 *
 * Plan de cartes :
 *   « parentheses » (Créé n°14) : tableau_vf sur les cinq écritures types du
 *     cadrage, vérités calculées (dont -(a - b) = -a - b : FAUX car b ≠ 0).
 *   « signes » (Créé n°15) : les trois significations du signe − dans
 *     -(-a) - b — relier + texte_trou_drag (menu de significations).
 *   « correctes » (Créé n°16) : qcm_multiple — coche les écritures correctes
 *     (règle : deux signes ne peuvent pas se suivre sans parenthèse).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 711;
const L_PARENTHESES = 'Parenthèses obligatoires ou pas ?';
const L_SIGNES = 'Les trois significations du signe −';
const L_CORRECTES = 'Écritures correctes ou incorrectes';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const SIGNIF_OPPOSE = 'l\'opposé d\'un nombre';
const SIGNIF_NEGATIF = 'le signe d\'un nombre négatif';
const SIGNIF_SOUSTRACTION = 'la soustraction entre deux nombres';

// ═════════ Carte 1 — V/F sur les écritures types (Créé n°14) ═════════

const genParenthesesVf = () => {
    const a = randInt(2, 9);
    let b = randInt(2, 9);
    if (b === a) b = (a % 9) + 1;
    const vraies = [
        [`Dans ${a} + (-${b}), on peut écrire plus simplement ${a} - ${b}.`, 0],
        [`-(${a} + ${b}) désigne l'opposé de la somme ${a} + ${b}.`, 0],
        [`-(-${a}) = ${a}.`, 0],
    ];
    const fausses = [
        [`Dans ${a} - (-${b}), on peut supprimer les parenthèses.`, 1],
        [`-(${a} - ${b}) = -${a} - ${b}.`, 1],
    ];
    const lignes = shuffle([...shuffle(vraies).slice(0, 2), ...shuffle(fausses).slice(0, 2)]);
    const payload = makeVraiFaux(
        lignes.map(l => l[0]),
        lignes.map(l => l[1]),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_PARENTHESES));
    payload._meta.params = { a, b };
    return payload;
};

// ═════════ Carte 2 — les trois signes (Créé n°15) ═════════

const exprSignes = () => {
    const a = randInt(2, 12);
    const b = randInt(2, 12);
    return { a, b, txt: `-(-${a}) - ${b}` };
};

const genSignesRelier = () => {
    const e = exprSignes();
    const pairs = [
        { source: '1er signe −, devant la parenthèse', target: SIGNIF_OPPOSE },
        { source: '2e signe −, dans la parenthèse', target: SIGNIF_NEGATIF },
        { source: '3e signe −, entre les nombres', target: SIGNIF_SOUSTRACTION },
    ];
    const payload = makeRelier(pairs,
        `<p>Dans l'expression <strong>${e.txt}</strong>, on rencontre trois signes « − ». `
        + '<b>Associe chaque signe à sa signification.</b></p>',
        opts(L_SIGNES));
    payload._meta.params = { a: e.a, b: e.b };
    return payload;
};

const genSignesDrag = () => {
    const e = exprSignes();
    const phrase = `Dans ${e.txt} : le signe devant la parenthèse indique {{${SIGNIF_OPPOSE}}} ; `
        + `celui dans la parenthèse est {{${SIGNIF_NEGATIF}}} ; celui entre les nombres est {{${SIGNIF_SOUSTRACTION}}}.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète avec la signification de chaque signe −.</b></p>',
        {
            ...opts(L_SIGNES), mode: 'drag',
            distracteurs: filtrerDistracteurs(phrase, ['la multiplication', 'le signe d\'un nombre positif']),
        });
    payload._meta.params = { a: e.a, b: e.b };
    return payload;
};

// ═════════ Carte 3 — écritures correctes (Créé n°16) ═════════

const genCorrectesQcmMultiple = () => {
    const a = randInt(2, 9);
    let b = randInt(2, 9);
    if (b === a) b = (a % 9) + 1;
    const corrects = shuffle([
        `${a} - (-${b})`, `${a} + (-${b})`, `-(${a} + ${b})`, `${a} - (+${b})`,
    ]).slice(0, 3);
    const incorrects = shuffle([
        `${a} - -${b}`, `${a} + -${b}`, `${a} + + ${b}`,
    ]).slice(0, 3);
    const payload = makeQcm([...corrects, ...incorrects], corrects,
        '<p><b>Coche les trois écritures mathématiques correctes.</b> '
        + '(Rappel : deux signes ne peuvent pas se suivre sans parenthèse.)</p>',
        opts(L_CORRECTES), true);
    payload._meta.params = { a, b, corrects };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    parentheses_vf: genParenthesesVf,
    signes_relier: genSignesRelier,
    signes_texte_trou_drag: genSignesDrag,
    correctes_qcm_multiple: genCorrectesQcmMultiple,
};

const FORMAT_LABELS = {
    vf: 'Vrai/Faux', relier: 'Relier', texte_trou_drag: 'Drag', qcm_multiple: 'QCM Multi',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'parentheses', label: L_PARENTHESES, formatsList: buildFormats('parentheses') },
    { id: 'signes', label: L_SIGNES, formatsList: buildFormats('signes') },
    { id: 'correctes', label: L_CORRECTES, formatsList: buildFormats('correctes') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
